/**
 * Reading values out of a parsed element.
 *
 * MusicXML is optional almost everywhere: a measure may state no attributes, a note may
 * carry no stem, a part may declare no transposition. Every read here answers with a
 * fallback rather than a throw, so an importer reads like the format's own prose --
 * "the divisions if it says any, otherwise what was in force" -- instead of a tower of
 * existence checks that all have to be right.
 */

import type { XmlElement } from "./parse";

/** The first child element with this name, or `null`. */
export function child(element: XmlElement | null, name: string): XmlElement | null {
  return element?.children.find((candidate) => candidate.name === name) ?? null;
}

/** Every child element with this name, in document order. */
export function childrenNamed(element: XmlElement | null, name: string): readonly XmlElement[] {
  return element?.children.filter((candidate) => candidate.name === name) ?? [];
}

/** Follow a chain of child names, e.g. `path(root, "work", "work-title")`. */
export function path(element: XmlElement | null, ...names: readonly string[]): XmlElement | null {
  let here = element;
  for (const name of names) here = child(here, name);
  return here;
}

/** An attribute, or `null`. */
export function attr(element: XmlElement | null, name: string): string | null {
  return element?.attrs[name] ?? null;
}

/** An attribute as a number, or `fallback` when it is absent or not a number. */
export function attrNumber(element: XmlElement | null, name: string, fallback: number): number {
  const raw = attr(element, name);
  if (raw === null) return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

/** The text of a named child, or `null` when there is no such child. */
export function textOf(element: XmlElement | null, name: string): string | null {
  return child(element, name)?.text ?? null;
}

/** The text of a named child as a number, or `fallback`. */
export function numberOf(element: XmlElement | null, name: string, fallback: number): number {
  const raw = textOf(element, name);
  if (raw === null || raw.trim() === "") return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

/** Whether a named child is present at all; MusicXML uses bare elements as flags. */
export function has(element: XmlElement | null, name: string): boolean {
  return child(element, name) !== null;
}

/** The text of a named child when it is one of `allowed`, otherwise `fallback`. */
export function oneOf<T extends string>(
  element: XmlElement | null,
  name: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const raw = textOf(element, name);
  return allowed.includes(raw as T) ? (raw as T) : fallback;
}

/** An attribute when it is one of `allowed`, otherwise `fallback`. */
export function attrOneOf<T extends string>(
  element: XmlElement | null,
  name: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const raw = attr(element, name);
  return allowed.includes(raw as T) ? (raw as T) : fallback;
}
